import { Button } from "@/components/ui";
import { Pencil } from "lucide-react";
import React, { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { FilterReview } from "./BulkMessageReviewPage/FilterReview";
import { useBulkMessageProposal } from "@/hooks/useBulkMessageProposal";
import { useOrg } from "@/hooks/useOrg";
import { useFormContext } from "react-hook-form";
import { formToFilterApi } from "./utils/mutationBuilders";
import { FormValues } from "./types";
import { Spinner } from "@/assets/svg/Spinner";
import { StudentBar } from "./StudentBar";
import { i18n } from "@/i18n";
import { ComposeReview } from "./BulkMessageReviewPage/ComposeReview";
import { BulkMessageOutletContext } from "./SendBulkMessage";
import { useProtectedNavigation } from "@/routes/useProtectedNavigation";
import { RouteId } from "@/routes/getRoute";

export function BulkMessageReviewPage() {
  const form = useFormContext<FormValues>();
  const { org, loading: orgIdLoading } = useOrg();
  const { getValues } = form;

  const { csvFile } = useOutletContext<BulkMessageOutletContext>();

  const { setStudentCount } = useOutletContext<BulkMessageOutletContext>();

  const { students, loading: proposalLoading } = useBulkMessageProposal({
    orgId: org?.id,
    filters: formToFilterApi(getValues("filters")),
  });

  useEffect(
    () => setStudentCount(students.length),
    [setStudentCount, students.length],
  );

  return (
    <div className="flex flex-col p-6 pb-0">
      <div className="flex border-b pt-3">
        {orgIdLoading || proposalLoading ? (
          <div className="flex h-30 w-full flex-col items-center justify-center gap-2">
            <Spinner className="size-4 fill-white text-black/50" />
            <p>
              {i18n.t(
                "bulkMessaging.basicInformation.studentFilters.loadingStudents",
              )}
            </p>
          </div>
        ) : (
          <div className="flex w-full flex-col gap-6 pb-6">
            <p>
              <Trans
                i18nKey="bulkMessaging.review.almostDone"
                values={{ count: students.length.toLocaleString() }}
              >
                You&apos;re almost done! This campaign will send a message to
                <strong className="font-medium"></strong> students.
              </Trans>
            </p>
            <div className="flex gap-8">
              <div className="flex grow">
                <StudentBar students={students} header={false} />
              </div>
              {csvFile && getValues("filters.userIds")?.length && (
                <div className="gap- flex items-center gap-6">
                  <p className="text-sm text-foreground/40">{csvFile?.name}</p>
                  <EditButton path="bulkMessageBasicInfo" />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="flex gap-2 border-b py-6 pb-10">
        <div className="flex grow">
          <FilterReview />
        </div>
        <EditButton path="bulkMessageBasicInfo" />
      </div>
      <div className="flex gap-2 pt-10">
        <div className="flex grow">
          <ComposeReview />
        </div>
        <EditButton path="bulkMessageCompose" />
      </div>
    </div>
  );
}

function EditButton({ path }: { path: RouteId }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "bulkMessaging.review",
  });
  const { navToRoute } = useProtectedNavigation();

  return (
    <Button type="button" onClick={() => navToRoute(path)} variant="outline">
      <Pencil />
      {t("edit")}
    </Button>
  );
}
